import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import BackButton from "../../../components/buttons/BackButton";
import Button from "../../../components/buttons/button";
import CustomDropdown from "../../../components/dropdown/CustomDropdown";
import Label from "../../../components/labels/Labels";
import { useCreateAzanTimeMutation, useGetazantimeQuery } from "../../../store/Reducer/prayertimes";
import { setUserprayerchange } from "../../../store/slices/user";
import PrayerTimes from "../user-settings/prayer-times";

const calculationMethodOptions = [
  { value: "0", label: "Jafari / Shia Ithna-Ashari" },
  { value: "1", label: "University of Islamic Sciences, Karachi" },
  { value: "2", label: "Islamic Society of North America" },
  { value: "3", label: "Muslim World League" },
  { value: "4", label: "Umm Al-Qura University, Makkah" },
  { value: "5", label: "Egyptian General Authority of Survey" },
  { value: "7", label: "Institute of Geophysics, University of Tehran" },
  { value: "8", label: "Gulf Region" },
  { value: "9", label: "Kuwait" },
  { value: "10", label: "Qatar" },
  { value: "11", label: "Majlis Ugama Islam Singapura, Singapore" },
  { value: "12", label: "Union Organization Islamic de France" },
  { value: "13", label: "Diyanet İşleri Başkanlığı, Turkey" },
  { value: "14", label: "Spiritual Administration of Muslims of Russia" },
  { value: "15", label: "Moonsighting Committee Worldwide" },
  { value: "16", label: "Dubai (experimental)" },
  { value: "17", label: "Jabatan Kemajuan Islam Malaysia (JAKIM)" },
  { value: "18", label: "Tunisia" },
  { value: "19", label: "Algeria" },
  { value: "20", label: "KEMENAG - Kementerian Agama Republik Indonesia" },
  { value: "21", label: "Morocco" },
  { value: "22", label: "Comunidade Islamica de Lisboa" },
  { value: "23", label: "Ministry of Awqaf, Islamic Affairs and Holy Places, Jordan" },
];

const asrMethodOptions = [
  { value: "0", label: "Shafi (earlier Asr)" },
  { value: "1", label: "Hanafi (later Asr)" },
];

const latitudeRuleOptions = [
  { value: "1", label: "Middle of the Night" },
  { value: "2", label: "One Seventh" },
  { value: "3", label: "Angle Based" },
];

const find = (options, value) => options.find((o) => o.value === String(value ?? ""));

const PrayerTimeAthanCalculation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const now = new Date();
  // The saved settings, so the form opens on what the masjid uses now.
  const { data: saved, isLoading: loadingSaved } = useGetazantimeQuery({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [createAzanTime, { isLoading }] = useCreateAzanTimeMutation();

  const [method, setMethod] = useState(null);
  const [school, setSchool] = useState(null);
  const [latitudeRule, setLatitudeRule] = useState(null);

  useEffect(() => {
    const c = saved?.data?.calculations;
    if (!c) return;
    setMethod((v) => v || find(calculationMethodOptions, c.calculationMethod) || null);
    setSchool((v) => v || find(asrMethodOptions, c.school) || null);
    setLatitudeRule((v) => v || find(latitudeRuleOptions, c.higherLatitudeMethod) || null);
  }, [saved]);

  const usesImport = saved?.data?.custom;
  const complete = method && school && latitudeRule;
  const preview = complete
    ? { calculationMethod: Number(method.value), school: Number(school.value), higherLatitudeMethod: Number(latitudeRule.value) }
    : null;

  const handleSave = async () => {
    if (!complete) {
      toast.error("Choose a calculation method, an Asr school and a high latitude rule.");
      return;
    }
    try {
      await createAzanTime(preview).unwrap();
      dispatch(setUserprayerchange(false));
      toast.success("Athan settings saved");
      navigate("/user-dashboard/prayer-times/athan");
    } catch (error) {
      toast.error(error?.data?.message || "Couldn't save the athan settings.");
    }
  };

  return (
    <>
      <section className="py-4 px-4 w-full">
        <div className="w-full mx-auto pt-3 pb-4 px-3">
          <div className="text-start mb-4">
            <h1 className="text-2xl text-start font-semibold text-slate-800 flex items-center gap-2">
              <BackButton /> Athan calculation
            </h1>
          </div>

          <p className="py-3 px-3 text-start text-md font-normal border border-blue-500 bg-blue-100 text-blue-600 rounded-lg mb-6">
            Choose how the athan times are calculated. The table below shows the times with your choices; press Save to use them.
          </p>

          {usesImport && (
            <p role="alert" className="py-3 px-3 mb-6 text-sm rounded-lg border border-amber-400 bg-amber-50 text-amber-800">
              Your athan times now come from an imported timetable. Saving here switches to calculated times.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="calculation-method" text="Calculation method" />
              <CustomDropdown
                title={loadingSaved ? "Loading…" : "Select method"}
                options={calculationMethodOptions}
                onSelect={setMethod}
                selectedOption={method?.label}
              />
            </div>
            <div>
              <Label htmlFor="asr-school" text="Asr time" />
              <CustomDropdown
                title={loadingSaved ? "Loading…" : "Select school"}
                options={asrMethodOptions}
                onSelect={setSchool}
                selectedOption={school?.label}
              />
            </div>
            <div>
              <Label htmlFor="high-latitude-rule" text="High latitude rule" />
              <CustomDropdown
                title={loadingSaved ? "Loading…" : "Select rule"}
                options={latitudeRuleOptions}
                onSelect={setLatitudeRule}
                selectedOption={latitudeRule?.label}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button text={t("cancel") || "Cancel"} type="secondary" className="w-32" onClick={() => navigate(-1)} />
            <Button text={t("save")} type="primary" className="w-32" onClick={handleSave} loading={isLoading} disabled={!complete} />
          </div>
        </div>
      </section>

      <PrayerTimes preview={preview} />
    </>
  );
};

export default PrayerTimeAthanCalculation;
